import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BookOpen, Sparkles } from 'lucide-react';
import { GuidesFilters } from '@/hooks/useGuides';
import { useDebounce } from '@/hooks/useDebounce';
import { useTranslation } from '@/hooks/useTranslation';
import GuidesFiltersBar from '@/components/guides/GuidesFiltersBar';
import GuidesContent from '@/components/guides/GuidesContent';
import PageBackground from '@/components/common/PageBackground';
import { NestedCategoryFilter } from '@/components/base';

const GuidesPage: React.FC = () => {
  const { t } = useTranslation('guides');
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);

  // Initialize selectedProductId from query params
  const productIdFromUrl = searchParams.get('productId');
  const [selectedProductId, setSelectedProductId] = useState<string | undefined>(productIdFromUrl || undefined);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');

  // Sync state with URL params when URL changes (e.g., from navigation)
  useEffect(() => {
    const productIdParam = searchParams.get('productId');
    // Only update if URL param differs from current state (prevents loops)
    if (productIdParam !== (selectedProductId || null)) {
      setSelectedProductId(productIdParam || undefined);
    }
  }, [searchParams.toString()]);

  // Update URL when product filter changes from UI
  const handleProductChange = useCallback(
    (productId: string | undefined) => {
      setSelectedProductId(productId);
      const newSearchParams = new URLSearchParams(searchParams);
      if (productId) {
        newSearchParams.set('productId', productId);
      } else {
        newSearchParams.delete('productId');
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const filters: GuidesFilters = useMemo(
    () => ({
      limit: 12,
      language: selectedLanguage === 'all' ? 'en' : selectedLanguage,
      productId: selectedProductId === 'all' ? undefined : selectedProductId,
      categoryIds: selectedCategoryIds.length > 0 ? selectedCategoryIds : undefined,
      isPublished: true, // Only show published guides for user-facing page
      search: debouncedSearch || undefined,
      useDraftData: false, // Use snapshot data for published guides
    }),
    [selectedProductId, selectedCategoryIds, selectedLanguage, debouncedSearch],
  );

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedProductId(undefined);
    setSelectedCategoryIds([]);
    setSelectedLanguage('all');
    // Clear productId from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('productId');
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const hasActiveFilters = !!(
    searchQuery ||
    selectedProductId ||
    selectedCategoryIds.length > 0 ||
    (selectedLanguage && selectedLanguage !== 'all')
  );

  return (
    <PageBackground>
      {/* Modern Header */}
      <div className="mb-12 text-center sm:text-left">
        <div className="inline-flex items-center gap-3 mb-4">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl blur-lg opacity-50" />
            <div className="relative p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent">
              {t('title')}
            </h1>
            <div className="flex items-center gap-2 mt-2">
              <Sparkles className="w-4 h-4 text-indigo-500" />
              <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">{t('subtitle')}</p>
            </div>
          </div>
        </div>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mt-4 leading-relaxed">{t('description')}</p>
      </div>

      {/* Filters */}
      <GuidesFiltersBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedProductId={selectedProductId}
        onProductChange={handleProductChange}
        selectedLanguage={selectedLanguage}
        onLanguageChange={setSelectedLanguage}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Main Content with Category Sidebar */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Category Sidebar */}
        <aside className="lg:w-72 flex-shrink-0">
          <div className="lg:sticky lg:top-4">
            <NestedCategoryFilter selectedCategoryIds={selectedCategoryIds} onChange={setSelectedCategoryIds} />
          </div>
        </aside>

        {/* Guides Content */}
        <GuidesContent
          filters={filters}
          selectedLanguage={selectedLanguage}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
        />
      </div>
    </PageBackground>
  );
};

export default GuidesPage;
